import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { SEOHead } from '@/components/SEOHead';
import { Scale, Mail, MapPin, Building } from 'lucide-react';

export const LegalNoticePage = () => {
  const { t, language } = useLanguage();

  const content = language === 'fr' ? {
    title: "Mentions légales",
    lastUpdated: "Dernière mise à jour : 24 juin 2025",
    sections: [
      {
        title: "1. Informations sur l'éditeur",
        content: `Raison sociale : Gaius
        Siège social : Paris, France
        Email : contact@gaiustech.com
        Directeur de publication : M. Paul-Henri Levivier
        
        Numéro SIREN : 803876457`
      },
      {
        title: "2. Hébergement du site",
        content: `Le site est hébergé par :
        Lovable
        https://lovable.dev/`
      },
      {
        title: "3. Propriété intellectuelle",
        content: `Le site Gaius et l'ensemble de son contenu (textes, images, logos, mise en page, etc.) sont protégés par le Code de la propriété intellectuelle.
        
        Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation écrite préalable de Gaius.
        
        Les marques et logos présents sur le site sont déposés par Gaius ou par des tiers. Toute reproduction totale ou partielle de ces marques ou logos, effectuée à partir des éléments du site sans l'autorisation expresse de Gaius ou du titulaire est donc prohibée.`
      },
      {
        title: "4. Responsabilité",
        content: `Gaius s'efforce d'assurer au mieux de ses possibilités, l'exactitude et la mise à jour des informations diffusées sur ce site.
        
        Toutefois, Gaius ne peut garantir l'exactitude, la complétude et l'actualité des informations diffusées sur son site.
        
        En conséquence, l'utilisateur reconnaît utiliser ces informations sous sa responsabilité exclusive.`
      },
      {
        title: "5. Loi applicable",
        content: `Le présent site est soumis au droit français.
        
        En cas de litige, et à défaut de résolution amiable, les tribunaux français seront seuls compétents.`
      },
      {
        title: "6. Contact",
        content: `Pour toute question relative aux mentions légales, vous pouvez nous contacter :
        
        Par email : contact@gaiustech.com
        Par courrier : Gaius, Paris, France`
      },
      {
        title: "7. Cookies et données personnelles",
        content: `Pour toute information concernant les cookies et le traitement de vos données personnelles, nous vous invitons à consulter :
        
        • Notre Politique de confidentialité
        • Nos Conditions générales d'utilisation`
      }
    ]
  } : {
    title: "Legal Notice",
    lastUpdated: "Last updated: June 24, 2025",
    sections: [
      {
        title: "1. Publisher Information",
        content: `Company name: Gaius
        Headquarters: Paris, France
        Email: contact@gaiustech.com
        Publication Director: M. Paul-Henri Levivier
        
        SIREN number: 803876457`
      },
      {
        title: "2. Website Hosting",
        content: `The site is hosted by:
        Lovable
        https://lovable.dev/`
      },
      {
        title: "3. Intellectual Property",
        content: `The Gaius site and all its content (texts, images, logos, layout, etc.) are protected by the Intellectual Property Code.
        
        Any reproduction, representation, modification, publication, adaptation of all or part of the site elements, whatever the means or process used, is prohibited, except with prior written authorization from Gaius.
        
        The trademarks and logos present on the site are registered by Gaius or by third parties. Any total or partial reproduction of these trademarks or logos, made from site elements without express authorization from Gaius or the holder is therefore prohibited.`
      },
      {
        title: "4. Liability",
        content: `Gaius strives to ensure, to the best of its ability, the accuracy and updating of information disseminated on this site.
        
        However, Gaius cannot guarantee the accuracy, completeness and timeliness of information disseminated on its site.
        
        Consequently, the user acknowledges using this information under their exclusive responsibility.`
      },
      {
        title: "5. Applicable Law",
        content: `This site is subject to French law.
        
        In case of dispute, and failing amicable resolution, French courts will have exclusive jurisdiction.`
      },
      {
        title: "6. Contact",
        content: `For any questions regarding legal notices, you can contact us:
        
        By email: contact@gaiustech.com
        By mail: Gaius, Paris, France`
      },
      {
        title: "7. Cookies and Personal Data",
        content: `For any information concerning cookies and the processing of your personal data, we invite you to consult:
        
        • Our Privacy Policy
        • Our Terms of Use`
      }
    ]
  };

  const seoTitle = content.title;
  const seoDescription = language === 'fr'
    ? "Mentions légales de Gaius - Formation IA Avocat. Informations légales, propriété intellectuelle et conditions d'utilisation de notre plateforme de formation en intelligence artificielle juridique."
    : "Legal notice for Gaius - Legal AI Training. Legal information, intellectual property and terms of use for our legal artificial intelligence training platform.";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": content.title,
    "description": seoDescription,
    "url": `https://gaius-tech.com/${language === 'fr' ? 'mentions-legales' : 'legal-notice'}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "logo": {
        "@type": "ImageObject",
        "url": "/lovable-uploads/1b5b9971-7f8c-4d09-a61e-d40ffa00ed01.png"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={language === 'fr' ? "https://gaius-tech.com/mentions-legales" : "https://gaius-tech.com/legal-notice"}
        alternateUrlFr="https://gaius-tech.com/mentions-legales"
        alternateUrlEn="https://gaius-tech.com/legal-notice"
        structuredData={structuredData}
      />
      
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {content.title}
            </h1>
            <p className="text-blue-100">{content.lastUpdated}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="space-y-8">
            {content.sections.map((section, index) => (
              <div key={index}>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {section.title}
                </h2>
                <div className="text-gray-700 whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center space-x-4 text-gray-600">
              <div className="flex items-center space-x-2">
                <Mail className="w-5 h-5" />
                <span>contact@gaiustech.com</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5" />
                <span>Paris, France</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
